<script>
    import { fade, fly } from 'svelte/transition';
    import Button from './Button.svelte';
    import * as t from '../translations/Common.ts';
    import { onDestroy } from 'svelte';

    const focusableElementTypes = `button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])`; 
    let modal;
    
    function onMount(element) {
        modal = element;
        
        const focusableElements = modal.querySelectorAll(focusableElementTypes);
        focusableElements.forEach((node) => {
            node.addEventListener("keydown", handleKeyDown);
        });
    }

    onDestroy(() => {
        const focusableElements = modal.querySelectorAll(focusableElementTypes);
        focusableElements.forEach((node) => {
            node.removeEventListener("keydown", handleKeyDown);
        });
    });
    
    function handleKeyDown(e) {
        const isTabPressed = e.key === `Tab` || e.keyCode === 9;

        if (!isTabPressed) {
            return;
        }
        
        const focusableElements = modal.querySelectorAll(focusableElementTypes);
        var focusedIndex = Array.prototype.indexOf.call(focusableElements, this);

        if (e.shiftKey) {
            focusedIndex--;
        } else {
            focusedIndex++;
        }

        if (focusedIndex > focusableElements.length -1) {
            focusedIndex = 0;
        } else if (focusedIndex < 0) {
            focusedIndex = focusableElements.length - 1;
        }

        focusableElements[focusedIndex].focus();
        e.preventDefault();
    }
    
    function closeWithEscape(e) {
        if (e.keyCode === 27) {
            isOpen = false;
        }
    }

    export let isOpen = false;
    export let closeOnConfirmInput = true;
    export let closeOnOutsideInput = false;
    
    export let headerText;
    export let confirmButtonText = '';
    export let cancelButtonText =  t.Cancel_Button();

    export let onOpen = () => {};
    export let onClose = () => {};
    export let onConfirm = () => {};
    export let onCancel = () => {};
    
    let isInitialFocusSet = false;
    let previousOpenState = false;
    function isOpenChanged(isOpen) {
        if (modal && !isInitialFocusSet) {
            setTimeout(() => {
                const focusableElements = modal.querySelectorAll(focusableElementTypes);
                focusableElements[0].focus();
                isInitialFocusSet = true;
            }, 0);
        }

        // ignore initial change from binding to component
        if (previousOpenState == isOpen)
            return;
        previousOpenState = isOpen;
        if (isOpen) {
            onOpen();
        }  else {
            isInitialFocusSet = false;
            onClose();
        }
    }
    
    function confirm() {
        onConfirm();
        if (closeOnConfirmInput) {
            isOpen = false;    
        }
    }
    
    function cancel(){
        onCancel();
        isOpen = false;
    }
    
    $: isOpenChanged(isOpen);
</script>

<!-- add keyboard event listeners to window while modal is open -->
<svelte:window on:keyup={closeWithEscape} />

<div role="dialog" 
     class="flex items-center justify-center fixed inset-0 vv-modal" 
     class:window-noscroll={isOpen} 
     class:hidden={!isOpen}>
    <div class="fixed inset-0 z-10 bg-black/50 vv-modal-overlay"
         aria-hidden="true"
         on:click={() => (closeOnOutsideInput ? isOpen = false : isOpen = isOpen)}
         transition:fade={{ duration: 100 }}
    ></div>

    <div use:onMount 
         class="bg-white relative p-5 z-20 vv-modal-content-container w-full lg:w-1/2" 
         transition:fly={{ y: -60, duration: 250 }}>
        {#if headerText}
            <h2 class="text-xl mb-4 font-bold border-b-2 border-solid border-[var(--vv-action-center-button-color)]">{headerText}</h2>
        {/if}
        <slot />
        
        <div class="flex justify-end mt-8">
            {#if confirmButtonText && confirmButtonText !== ''}
                <Button
                        type="action-center"
                        class="min-w-24 mr-4"
                        on:click={() => confirm()}>
                    {confirmButtonText}
                </Button>
            {/if}
            <Button
                type="secondary"
                class="min-w-24"
                on:click={() => cancel()}>
                {cancelButtonText}
            </Button>
        </div>
    </div>
</div>
